import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import {
  Col,
  Container,
  Row,
  CardBody,
  Card,
  TabContent,
  TabPane,
  Badge,
} from 'reactstrap';
import moment from 'moment';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';
import { withRouter, useHistory } from 'react-router-dom';
import fileDownload from 'js-file-download';
import Api from '../../util/api';
import exportToExcelFile from '../../util/xlsx';
import EventsForm from './components/EventsForm';
import Loading from '../../shared/components/custom/Loading';
import NavTab from '../../shared/components/custom/NavTab';
import EventsContactForm from './components/EventsContactForm';
import { scheduleEventTemplateMessages } from './eventMessageTemplates';
import t, { partial } from '../../util/translation/translation';
import EventsPariticipantForm from './components/EventsParticipantForm';
import { isValidUrl, normalizeName } from '../../util/functions';
import QuestionCard from './components/QuestionCard';
import { EventState } from './context';
import useConfig from '../../util/useConfig';
import EventTypes from './types';
import { filterGroupsLevels, filterTiers } from './accessFields';
import { getApiErrorMessage } from './series/seriesUtils';
import OccurrenceBanner from './series/components/OccurrenceBanner';
import AlertModal, {
  AlertModalType,
} from '../../shared/components/custom/AlertModal';
import LazyTabPane from '../../shared/components/custom/LazyTabPane';
import WaitlistPage from './components/WaitlistPage';
import CommentsListPage from '../comments/CommentsListPage';

// The participant/attendance endpoints return a specific validation error
// code (e.g. a guest-amount rule) as err.data.message. Map it to its
// translated message when possible, falling back to a generic one for
// anything else (network errors, unrecognized codes, plain string errors).
const getParticipantErrorMessage = (
  err,
  fallbackKey = 'shared.somethingWentWrong'
) => {
  if (err?.data?.message) {
    try {
      return t(`EventsForm.${err.data.message}`);
    } catch {
      // Unknown error code from the backend - fall through to the fallback.
    }
    // Event-wide codes, e.g. event.Cancelled on a cancelled series date
    const apiMessage = getApiErrorMessage(err, null);
    if (apiMessage) return apiMessage;
  }
  return t(fallbackKey);
};

const EventsFormPage = ({ eventForm = {} }) => {
  const [tab, setTab] = useState('');
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMount, setLoadingMount] = useState(false);
  const [eventCategories, setEventCategories] = useState([]);
  const [eventLevels, setEventLevels] = useState([]);
  const [eventGroups, setEventGroups] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [deletedParticipants, setDeletedParticipants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [inviteModelVisible, setInviteModelVisible] = useState(false);
  const [scheduleMessageID, setScheduleMessageID] = useState(null);
  const [scheduleMessagesForEvent, setScheduleMessagesForEvent] = useState([]);
  const [numberOfPendingMessagesPerEvent, setNumberOfPendingMessagesPerEvent] =
    useState([]);
  const [totalAmountOfScheduledMessages, setTotalAmountOfScheduledMessages] =
    useState(0);
  const [messageStatus, setMessageStatus] = useState('Scheduled');
  const [clearContactPage, setClearContactPage] = useState(false);
  const [startDate, setStartDate] = useState();
  const [questionsNanswers, setQuestionsNansewrs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState([]);
  const config = useConfig();
  const [eventTiers, setEventTiers] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [maxGuestAmount, setMaxGuestAmount] = useState(0);
  const [currentParticipant, setCurrentParticipant] = useState(null);
  const [commentsAccess, setCommentsAccess] = useState(null);
  const { eventID } = useParams();
  const history = useHistory();
  const p = partial('EventsFormPage');
  const f = partial('OfferForm');
  const getData = async () => {
    setLoading(true);
    try {
      // The event itself is fetched separately by getEvent (together with
      // its comment access) when eventID is set - fetching it again here
      // was a redundant duplicate request.
      const [resEventCategories, resEventLevels, resEventGroups, resTiers] =
        await Promise.all([
          Api.events.getEventCategories(),
          Api.club.getLevels(),
          Api.club.getGroups(),
          Api.tiers.allTiers(),
        ]);
      setEventCategories(
        resEventCategories.data.map((category) => {
          const { id, title, maxAttendees } = category;
          return {
            value: id,
            label: title,
            maximum: maxAttendees,
          };
        })
      );
      setEventLevels(
        resEventLevels.map((level) => {
          const { id, value } = level;
          return {
            value: id,
            label: value,
          };
        })
      );

      setEventGroups(
        resEventGroups.map((group) => {
          const { id, value } = group;
          return {
            value: id,
            label: value,
          };
        })
      );

      const mappedTiers = resTiers.data.map((tier) => ({
        label: tier.name,
        value: tier.id,
      }));

      setEventTiers(mappedTiers);

      setLoading(false);
    } catch (err) {
      toast.error(err || t('shared.somethingWentWrong'));
      setLoading(false);
    }
  };
  const getEvent = async () => {
    setLoading(true);
    try {
      const [resEvent, commentAccess] = await Promise.all([
        Api.events.show(eventID),
        Api.comments.getAccessForEntity(eventID),
      ]);
      setCommentsAccess(commentAccess);
      setEvent(resEvent.data);
      setLoading(false);
    } catch (err) {
      toast.error(err || t('shared.somethingWentWrong'));
      setLoading(false);
    }
  };

  const sortParticipants = (participantList) =>
    participantList.sort((a, b) =>
      moment(a.createdAt).diff(moment(b.createdAt), 'seconds')
    );
  const transformParticipantsToArray = (participantMap, clubCompanies) => {
    let allParticipants = [];
    let compParticipants = [];
    const _clubCompanies = [...clubCompanies];
    Object.keys(participantMap).forEach((key) => {
      switch (key) {
        case 'GUEST':
          allParticipants = [
            ...(participantMap[key] || []).map((part) => ({
              ...part,
              type: 'GUEST',
            })),
            ...allParticipants,
          ];
          break;
        case 'USER':
          allParticipants = [
            ...(participantMap[key] || []).map((part) => ({
              ...part,
              type: 'USER',
            })),
            ...allParticipants,
          ];
          break;
        default: {
          const foundCompanyIdx = clubCompanies.findIndex(
            (item) => item.id === key
          );
          if (foundCompanyIdx === -1) {
            break;
          }
          _clubCompanies[foundCompanyIdx].maxPartcipants =
            participantMap[key].maxAttendees;
          const foundCompany = _clubCompanies[foundCompanyIdx];
          compParticipants = [
            ...compParticipants,
            ...participantMap[key].participants.map((employee) => ({
              ...employee,
              company: foundCompany,
              type: 'COMPANY',
            })),
          ];
          break;
        }
      }
    });
    return {
      comp: _clubCompanies,
      particip: sortParticipants([...compParticipants, ...allParticipants]),
    };
  };

  const getQuestions = async (eventParticipants) => {
    setLoading(true);
    let allQuestions = [];
    try {
      const answers = await Api.questions.fetchAnswers(eventID);
      if (answers?.data.length <= 0 || !answers) {
        const domainQuestions =
          await Api.questions.fetchDomainSpecificQuestions(eventID);
        allQuestions = domainQuestions?.data?.question;
        if (domainQuestions.data === '') {
          setQuestions([]);
        } else {
          setQuestions(domainQuestions?.data?.question);
        }
        setUserAnswers([]);
      } else {
        const uniqueAllQuestions = answers.data.flatMap((q) =>
          q.answer.map((a) => a)
        );
        const hashmap = {};
        const uniqueArr = [];

        uniqueAllQuestions.forEach((obj) => {
          if (!hashmap[obj.id]) {
            hashmap[obj.id] = true;
            uniqueArr.push(obj);
          }
        });
        allQuestions = uniqueArr;
        setQuestions(uniqueArr);
        setUserAnswers(answers.data);
      }
      const result = eventParticipants
        .filter(
          (participant) =>
            participant.userId !== '' && participant.userId !== null
        )
        .map((participant) => {
          const answerObj =
            answers?.data?.find((a) => a.userId === participant.userId)
              ?.answer || [];
          let answersMap = {};

          if (Array.isArray(answerObj)) {
            const transformedAnswers = {};

            answerObj.forEach((ans) => {
              // Per-guest answers are exported with that guest's name and
              // email (see guestColumnsFor)
              if (ans.guest && Number.isInteger(ans.guestIndex)) {
                return;
              }
              const { answer, question } = ans;

              let uniqueQuestion = question;
              let counter = 1;
              while (transformedAnswers[uniqueQuestion] !== undefined) {
                uniqueQuestion = `${question}__..__${counter}`;
                counter += 1;
              }

              transformedAnswers[uniqueQuestion] = answer;
            });

            answersMap = transformedAnswers;
          } else {
            answersMap = answerObj;
          }

          const defaultAnswers = allQuestions.reduce(
            (acc, cur) => ({ ...acc, [cur.question]: '-- --' }),
            {}
          );

          const finalAnswers = { ...defaultAnswers, ...answersMap };

          return { [participant.userId]: finalAnswers };
        });
      setQuestionsNansewrs(result);
      setLoading(false);
    } catch (err) {
      toast.error(err || t('shared.somethingWentWrong'));
      setLoading(false);
    }
  };

  const getPaticipants = async () => {
    setLoading(true);
    try {
      const { data: participantsMap } =
        await Api.events.getParticipants(eventID);
      const { data: deletedUserParticipants } =
        await Api.events.getDeletedParticipants(eventID);
      const clubCompanies = await Api.companies
        .getCompanies(10000, 1, '', 'name', false, true, true, '')
        .then(({ data }) => data.data);
      const { comp, particip } = transformParticipantsToArray(
        participantsMap,
        clubCompanies
      );
      const mappedParticipants = particip.map((item) => ({
        ...item,
        showSave: false,
        toggled: false,
      }));
      const deletedUserWithCompany = deletedUserParticipants.map((dp) => {
        const companyEdit = { ...dp };
        if (companyEdit.companyId) {
          const companyName = clubCompanies.find(
            (c) => c.id === companyEdit.companyId
          );

          if (companyName) {
            companyEdit.company = companyName.name;
          }
        }
        return companyEdit;
      });
      setCompanies(comp);
      setParticipants(mappedParticipants);
      setDeletedParticipants(deletedUserWithCompany);
      getQuestions(mappedParticipants);
      setLoading(false);
    } catch (err) {
      toast.error(err || t('shared.somethingWentWrong'));
      setLoading(false);
    }
  };
  const getParticipantType = (values) => {
    if (values?.selectedUser) {
      return 'USER';
    }
    if (values?.selectedCompany) {
      return 'COMPANY';
    }
    return 'GUEST';
  };

  const handleInviteParticipant = async (values) => {
    const participantType = getParticipantType(values);
    const nonCompayUserPhone =
      typeof values?.userPhone === 'string'
        ? values?.userPhone
        : values?.userPhone?.phone;

    let payload = {};
    switch (participantType) {
      case 'COMPANY':
        payload = {
          userId: values.selectedEmployee.value.userId,
          companyId: values?.selectedCompany?.value?.id || null,
          firstname: values.selectedEmployee.value.firstName,
          lastname: values.selectedEmployee.value.lastName,
          phoneNumber: values.selectedEmployee.value.phone,
          email: values.selectedEmployee.value.email,
          participantType: 'USER',
          friendAmount: values.guestAmount ? Number(values.guestAmount) : 0,
          additionalNotes: '',
        };
        break;
      case 'USER':
        payload = {
          userId: values?.selectedUser?.userId,
          email: values?.selectedUser?.email,
          companyId: values?.selectedCompany?.value?.id || null,
          firstname: values.firstName,
          lastname: values.lastName,
          phoneNumber: nonCompayUserPhone,
          participantType: 'USER',
          friendAmount: values.guestAmount ? Number(values.guestAmount) : 0,
          additionalNotes: '',
        };
        break;
      case 'GUEST':
        payload = {
          userId: null,
          companyId: null,
          firstname: values.firstName,
          lastname: values.lastName,
          phoneNumber: values.phoneNumber,
          email: values.email,
          participantType: 'GUEST',
          friendAmount: values.guestAmount ? Number(values.guestAmount) : 0,
          additionalNotes: values?.comments || '',
        };
        break;
      default:
    }

    setLoading(true);
    try {
      if (currentParticipant) {
        await Api.events.updateParticipant(payload, currentParticipant.id);
      } else {
        await Api.events.addParticipants(payload, eventID);
      }
      getPaticipants();
      setInviteModelVisible(false);
      setCurrentParticipant(null);
      //  toast.success(t('EventParticipantsPage.participantCreated'));
    } catch (e) {
      setLoading(false);
      toast.error(e || t('shared.somethingWentWrong'));
    }
  };

  // Unified function to prepare participant data for export
  // An answer as text for the export: lists joined, reservations as
  // "<option> (×<places>)", nothing as "-- --"
  const formatExportAnswer = (answer, questionText) => {
    if (Array.isArray(answer)) {
      return answer.length > 0 ? answer.join(',') : '-- --';
    }
    if (answer && typeof answer === 'object' && answer.selectedItem) {
      const matchingQ = questions.find((q) => q.question === questionText);
      const option = matchingQ?.options?.find(
        (o) => o.id === answer.selectedItem
      );
      const room = option ? option.title : answer.selectedItem;
      return `${room} (×${answer.quantity || 1})`;
    }
    return answer || '-- --';
  };

  // Each guest's name, email and answers as a group of columns after the
  // questions: "Guest 1 – Name", "Guest 1 – Email", "Guest 1 – <question>"…
  // Only as many groups as the last guest with any of these.
  const guestColumnsFor = (exportParticipants) => {
    const guestAnswersOf = (participant) =>
      (
        userAnswers.find((a) => a.userId === participant.userId)?.answer || []
      ).filter((a) => a.guest && Number.isInteger(a.guestIndex));
    const hasText = (value) =>
      Array.isArray(value)
        ? value.length > 0
        : value !== undefined && value !== null && value !== '';

    // Questions asked of guests, in the order first answered
    const guestQuestions = [];
    let guestCount = 0;
    exportParticipants.forEach((participant) => {
      const guestAnswers = guestAnswersOf(participant);
      guestAnswers.forEach((a) => {
        if (!guestQuestions.some((q) => q.id === a.id)) {
          guestQuestions.push({ id: a.id, question: a.question });
        }
        if (hasText(a.answer)) {
          guestCount = Math.max(guestCount, a.guestIndex + 1);
        }
      });
      (participant.guests || []).forEach((guest, index) => {
        if (guest?.name || guest?.email) {
          guestCount = Math.max(guestCount, index + 1);
        }
      });
    });

    const columns = [];
    for (let i = 0; i < guestCount; i++) {
      const guest = p('guestLabel', { number: i + 1 });
      columns.push(
        { key: `guest${i}_name`, header: `${guest} – ${t('shared.name')}` },
        { key: `guest${i}_email`, header: `${guest} – ${t('shared.email')}` },
        ...guestQuestions.map((q) => ({
          key: `guest${i}_q_${q.id}`,
          header: `${guest} – ${q.question}`,
        }))
      );
    }

    const valuesFor = (participant) => {
      const values = {};
      const guestAnswers = guestAnswersOf(participant);
      for (let i = 0; i < guestCount; i++) {
        const isGuest = i < (participant.friendAmount || 0);
        values[`guest${i}_name`] = normalizeName(participant.guests?.[i]?.name);
        values[`guest${i}_email`] = participant.guests?.[i]?.email || '';
        guestQuestions.forEach((q) => {
          const entry = guestAnswers.find(
            (a) => a.id === q.id && a.guestIndex === i
          );
          values[`guest${i}_q_${q.id}`] = isGuest
            ? formatExportAnswer(entry?.answer, q.question)
            : '';
        });
      }
      return values;
    };

    return { columns, valuesFor };
  };

  const prepareParticipantsExportData = () => {
    const questionsWithUserId = questionsNanswers.map((question) => {
      const userId = Object.keys(question)[0];
      return { ...question[userId], userId };
    });

    const participantsAndAnswers = participants.map((participant) => {
      const participantAnswer = questionsWithUserId.find(
        (question) => question.userId === participant.userId
      );
      return { ...participant, answers: participantAnswer };
    });

    // Get all unique question fields (excluding userId)
    const questionFieldsSet = Array.from(
      new Set(
        questionsWithUserId.flatMap((question) => [...Object.keys(question)])
      )
    ).filter((question) => question !== 'userId');

    // Define base columns structure
    const baseColumns = {
      firstname: t('shared.firstname'),
      lastname: t('shared.lastname'),
      phone: t('shared.phone'),
      email: t('shared.email'),
      participantType: p('participantType'),
      numberOfGuests: t('EventParticipantsPage.numberOfGuests'),
      comments: t('shared.comments'),
      attendees: t('shared.attendees'),
      attended: t('shared.attended'),
      company: t('shared.company'),
      questions: t('shared.questions'),
    };

    const guestColumns = guestColumnsFor(participantsAndAnswers);

    // Process participants data
    const processedData = participantsAndAnswers.map((participant) => {
      const baseData = {
        // Names as typed can be all caps or all lower case; the export
        // gives every name part a capital first letter
        firstname: normalizeName(participant.firstname),
        lastname: normalizeName(participant.lastname),
        phone: participant.phoneNumber || '',
        email: participant.email || '',
        participantType: participant.participantType || '',
        numberOfGuests: participant.friendAmount || 0,
        comments: participant.additionalNotes || '',
        attendees: participant.friendAmount + 1 || 0,
        attended: participant.attendedAmount || 0,
        company: participant.company?.name || '',
        questions: '', // Empty column for questions
      };

      // Add question answers
      const questionAnswers = {};
      questionFieldsSet.forEach((questionField) => {
        const cleanQuestionName = questionField.split('__..__')[0];
        if (participant.type === 'GUEST') {
          questionAnswers[cleanQuestionName] = '-- --';
        } else {
          questionAnswers[cleanQuestionName] = formatExportAnswer(
            participant.answers?.[questionField],
            cleanQuestionName
          );
        }
      });

      return {
        ...baseData,
        ...questionAnswers,
        ...guestColumns.valuesFor(participant),
      };
    });

    return {
      data: processedData,
      columns: { ...baseColumns },
      questionFields: questionFieldsSet.map(
        (field) => field.split('__..__')[0]
      ),
      guestColumns: guestColumns.columns,
      totals: {
        totalParticipants: participants.reduce(
          (tot, curr) => tot + curr.friendAmount + 1,
          0
        ),
        totalAttended: participants.reduce(
          (tot, curr) => tot + curr.attendedAmount,
          0
        ),
        totalFriendCount: participants.reduce(
          (tot, curr) => tot + curr.friendAmount,
          0
        ),
      },
    };
  };

  const exportParticpants = async () => {
    setLoading(true);

    const separator = ';';

    try {
      const exportData = prepareParticipantsExportData();
      const { data, questionFields, guestColumns, totals } = exportData;

      const addTotalAsFinalRow = `${separator}${separator}${separator}${separator}${separator}${separator}${separator}${
        totals.totalParticipants
      }${separator}${totals.totalAttended}`;
      const finalRow = addTotalAsFinalRow.replaceAll(',', separator);

      const fields = `${t('shared.firstname')}${separator} ${t(
        'shared.lastname'
      )}${separator} ${t('shared.phone')}${separator} ${t('shared.email')}${separator} ${p(
        'participantType'
      )}${separator} ${t(
        'EventParticipantsPage.numberOfGuests'
      )}${separator} ${t('shared.comments')}${separator} ${t(
        'shared.attendees'
      )}${separator} ${t('shared.attended')}${separator} ${t(
        'shared.company'
      )}${separator} ${t('shared.questions')}${separator}${[
        ...questionFields,
        ...guestColumns.map((column) => column.header),
      ]
        .map((header) => ` ${header}`)
        .join(separator)}`;

      const values = data
        .map((participant) => {
          const answers = [
            ...questionFields.map(
              (questionField) => participant[questionField]
            ),
            ...guestColumns.map((column) => participant[column.key]),
          ].map((answer) => `"${answer ?? ''}"`);

          return [
            `"${participant.firstname}"`,
            `"${participant.lastname}"`,
            `"${participant.phone}"`,
            `"${participant.email}"`,
            `"${participant.participantType}"`,
            `"${participant.numberOfGuests}"`,
            `"${participant.comments}"`,
            `"${participant.attendees}"`,
            `"${participant.attended}"`,
            participant.company ? `"${participant.company}"` : '',
            '', // Empty column for questions
            answers.join(separator),
          ].join(separator);
        })
        .join('\n');

      const csv = `${fields}\n${values}\n${finalRow}`;

      fileDownload(csv, `${event.title} Event Participants.csv`);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err || `${p('listExportingERROR')}'!'`);
    }
  };

  const exportParticpantsXLSX = async () => {
    setLoading(true);

    try {
      const exportData = prepareParticipantsExportData();
      const { data, columns, questionFields, guestColumns, totals } =
        exportData;

      // Transform data for XLSX format with proper column headers
      const xlsxData = data.map((participant) => {
        const result = {};

        // Add base columns with translated headers
        result[columns.firstname] = participant.firstname;
        result[columns.lastname] = participant.lastname;
        result[columns.phone] = participant.phone;
        result[columns.email] = participant.email;
        result[columns.participantType] = participant.participantType;
        result[columns.numberOfGuests] = participant.numberOfGuests;
        result[columns.comments] = participant.comments;
        result[columns.attendees] = participant.attendees;
        result[columns.attended] = participant.attended;
        result[columns.company] = participant.company;

        // Add empty questions column as separator
        result[columns.questions] = '';

        // Add question columns with clean question names as headers
        questionFields.forEach((questionField) => {
          result[questionField] = participant[questionField];
        });

        // Each guest's name, email and answers
        guestColumns.forEach((column) => {
          result[column.header] = participant[column.key];
        });

        return result;
      });

      // Add summary row at the bottom
      const summaryRow = {};
      summaryRow[columns.firstname] = '';
      summaryRow[columns.lastname] = '';
      summaryRow[columns.phone] = '';
      summaryRow[columns.email] = '';
      summaryRow[columns.participantType] = '';
      summaryRow[columns.numberOfGuests] = '';
      summaryRow[columns.comments] = '';
      summaryRow[columns.attendees] = totals.totalParticipants;
      summaryRow[columns.attended] = totals.totalAttended;
      summaryRow[columns.company] = '';
      summaryRow[columns.questions] = '';

      // Add empty values for question columns in summary row
      questionFields.forEach((questionField) => {
        summaryRow[questionField] = '';
      });
      guestColumns.forEach((column) => {
        summaryRow[column.header] = '';
      });

      // Add the summary row to the data
      xlsxData.push(summaryRow);

      exportToExcelFile(xlsxData, `${event.title} Event Participants`);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err || `${p('listExportingERROR')}'!'`);
    }
  };

  const getScheduleMessagesByServiceId = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      let messageByEventId = [];

      if (messageStatus === t('ScheduledMessagesPage.filterByStatus')) {
        messageByEventId =
          await Api.scheduleMessages.getScheduleMessagesByServiceId(
            eventID,
            page,
            pageSize
          );
      } else {
        messageByEventId =
          await Api.scheduleMessages.getScheduleMessagesByServiceIdByStatus(
            eventID,
            page,
            pageSize,
            messageStatus
          );
      }

      if (messageStatus === 'Scheduled') {
        setNumberOfPendingMessagesPerEvent(messageByEventId?.data?.total);
      }

      setScheduleMessagesForEvent(messageByEventId.data.data);
      setTotalAmountOfScheduledMessages(messageByEventId.data.total);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err || `${t('ScheduledMessagesPage.GetByOfferIdError')}`);
    }
  };

  const sendMessage = async (values) => {
    const payload = {
      messageTitle: values.title,
      messageBody: values.content,
      type: values.type,
      receiverType: values.receiverType,
      scheduledDate:
        values.timeToSend === 'SCHEDULED'
          ? moment.utc(startDate).format()
          : null,
      clubName: config.cloudIdName,
    };

    let resCount = '';
    try {
      if (values.timeToSend === 'SCHEDULED' && scheduleMessageID) {
        resCount = await Api.events.updateMessage(
          eventID,
          payload,
          scheduleMessageID
        );

        setClearContactPage(true);
        setScheduleMessageID(null);
      } else if (values.timeToSend === 'INSTANT') {
        resCount = await Api.events.sendMessage(eventID, payload);
        setClearContactPage(true);
        setScheduleMessageID(null);
      } else {
        resCount = await Api.events.scheduleMessage(eventID, payload);
        setClearContactPage(true);
        setScheduleMessageID(null);
      }
      if (values.receiverType === 'PRIVILAGED') {
        toast.success(
          `${p('messageWillBeSentTo')} ${
            resCount.data === 1
              ? `${resCount.data} ${t('shared.person')}`
              : `${t('EventsContactForm.invited')}`
          }`
        );
      } else if (values.receiverType === 'ALL') {
        toast.success(
          `${p('messageWillBeSentTo')}  ${t('shared.participants')}/${t(
            'EventsContactForm.invited'
          )}`
        );
      } else {
        toast.success(
          `${p('messageWillBeSentTo')} ${
            resCount.data === 1
              ? `${resCount.data} ${t('shared.person')}`
              : `${t('shared.participants')}`
          }`
        );
      }
    } catch (err) {
      toast.error(err || t('shared.somethingWentWrong'));
    }
    getScheduleMessagesByServiceId();
  };

  useEffect(() => {
    // getData/getEvent/getPaticipants/getScheduleMessagesByServiceId all
    // toggle the shared `loading` flag independently, so whichever settles
    // first was clearing the overlay while the others were still in
    // flight (the form would appear ready with half-populated fields).
    // loadingMount stays true for the whole batch regardless of that
    // flickering, without touching each function's own standalone use of
    // `loading` elsewhere in this file.
    const loadInitialData = async () => {
      setLoadingMount(true);
      const tasks = [getData()];
      if (eventID) {
        tasks.push(
          getEvent(),
          getPaticipants(),
          getScheduleMessagesByServiceId()
        );
      }
      await Promise.all(tasks);
      setLoadingMount(false);
    };
    loadInitialData();
    // eslint-disable-next-line
  }, [eventID, messageStatus]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const onSubmit = async (values) => {
    const accessLevels = filterGroupsLevels(
      values?.eventLevels,
      values.targetAudience
    );
    const accessCategories = filterGroupsLevels(
      values?.eventGroups,
      values.targetAudience
    );
    const accessTiers = filterTiers(values?.eventTiers, values.targetAudience);
    if (
      values.targetAudience === EventTypes.TargetAudience.partner ||
      values.targetAudience === EventTypes.TargetAudience.both
    ) {
      if (
        (!accessLevels && !accessCategories) ||
        (accessLevels === null && !accessCategories === null)
      ) {
        setErrorMessage(f('levelOrGroupRequiredMessage'));
        setShowPermissionModal(true);
        return;
      }
    }
    if (values.targetAudience === EventTypes.TargetAudience.private) {
      if (!accessTiers || accessTiers === null) {
        setErrorMessage(f('levelOrGroupRequiredMessage'));
        setShowPermissionModal(true);
        return;
      }
    }
    const payload = {
      title: values.title,
      mapsUrl: values.mapsUrl,
      address: values.address,
      zip: values.zip,
      description: values.description,
      ...(!values.eventCategories?.value && {
        maxAttendees: Number(values.maxAttendees),
        maxAccountAttendees:
          Number(values.maxAccountAttendees) === 0 || null
            ? Number(values.maxAttendees)
            : Number(values.maxAccountAttendees),
      }),
      startDate: new Date(values.startDate).toISOString(),
      endDate: new Date(values.endDate).toISOString(),
      registrationStart: values.registrationStart
        ? new Date(values.registrationStart).toISOString()
        : null,
      registrationEnd: values.registrationEnd
        ? new Date(values.registrationEnd).toISOString()
        : null,
      categoryId: values.eventCategories?.value,
      accessLevels,
      accessCategories,
      accessTiers,
      targetAudience: values.targetAudience,
      feedVisibility: values.feedVisibility === 'ALL',
      shouldNotify: values.shouldNotify === 'YES',
      notify_user: values.notify_user,
      urlDescription: values.urlDescription,
      urlText: values.urlText,
      waitlistEnabled: values.waitlistEnabled,
      bindingRegistration: !!values.bindingRegistration,
      externalBooking: !!values.externalBooking,
      externalBookingUrl: values.externalBookingUrl?.trim() || null,
      externalBookingLabel: values.externalBookingLabel?.trim() || null,
      participantsVisible: values.participantsVisible,
      // A series occurrence is never copied weekly: the series makes its
      // dates, so the field isn't sent at all
      ...(!event?.seriesId && {
        recurringWeeksCount: values.recurringWeeksCount
          ? Number(values.recurringWeeksCount)
          : null,
      }),
      ...(isValidUrl(values.websiteUrl) &&
        values.websiteUrl !== '' && {
          websiteUrl: values.websiteUrl,
        }),
      ...(selectedCountry !== null && {
        country: selectedCountry,
      }),
      ...(values.city !== '' && {
        city: values.city,
      }),
    };
    setLoading(true);
    try {
      const image = values.imageUrl;
      // if (accessLevels === null && accessCategories === null) {
      //   toast.error(t('EventCategories.groupOrLevelMustBeSelected'));
      //   throw new Error(t('EventCategories.groupOrLevelMustBeSelected'));
      // }
      if (eventID) {
        await Api.events.update(payload, eventID);
        if (image) await image.promise?.('event', 'imageUrl', eventID);
        if (commentsAccess) {
          await Api.comments.updateAccessForEntity(eventID, {
            tiers: accessTiers,
            groups: accessCategories,
            levels: accessLevels,
            noAccessView: values.feedVisibility === 'ALL',
            visible: values.commentsEnabled,
          });
        } else {
          await Api.comments.createAccessForEntity(eventID, {
            tiers: accessTiers,
            groups: accessCategories,
            levels: accessLevels,
            noAccessView: values.feedVisibility === 'ALL',
            visible: values.commentsEnabled,
          });
        }
        toast.success(p('eventUpdated'));
        setLoading(false);
      } else {
        const { data: resEvent } = await Api.events.create(payload);
        if (image) await image.promise?.('event', 'imageUrl', resEvent.id);
        // The ticked ready-made messages, for it and any weekly copies
        const failedMessages = await scheduleEventTemplateMessages({
          eventId: resEvent.id,
          copyIds: resEvent.recurringEventIds,
          values,
          clubName: config.cloudIdName,
        });
        if (failedMessages) {
          toast.warning(
            t('EventsContactForm.scheduleMessagesFailed', {
              count: failedMessages,
            })
          );
        }
        await Api.comments.createAccessForEntity(resEvent.id, {
          tiers: accessTiers,
          groups: accessCategories,
          levels: accessLevels,
          noAccessView: values.feedVisibility === 'ALL',
          visible: values.commentsEnabled,
        });
        toast.success(p('eventCreated'));
        history.replace('/events');
      }
    } catch (err) {
      setLoading(false);
      toast.error(err || t('shared.somethingWentWrong'));
    }
  };

  const checkCount = async (values) => {
    const payload = {
      type: values.type,
      to: values.to,
      send_count: true,
    };
    setLoading(true);
    try {
      const resCount = await Api.events.sendMessage(eventID, payload);
      toast.success(
        `${p('messageWillBeSentTo')} ${
          resCount.data === 1
            ? `${resCount.data} ${t('shared.person')}`
            : `${resCount.data} ${t('shared.persons')}`
        }`
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err || t('shared.somethingWentWrong'));
    }
  };

  const changeGuestAmount = async (newGuests, participant) => {
    try {
      setLoading(true);
      // Guests are left out: the server drops the details of removed guests
      // itself, and this page's copy may be stale or lack the emails
      const { guests, ...rest } = participant;
      const payload = {
        ...rest,
        friendAmount: newGuests,
      };
      await Api.events.updateParticipant(payload, participant.id);
      toast.success(p('guestAmountUpdated'));
      const participantsCopy = [...participants];
      const foundItemIndex = participantsCopy.findIndex(
        (item) => item.id === participant.id
      );
      participantsCopy[foundItemIndex].friendAmount = newGuests;
      if (Array.isArray(guests)) {
        participantsCopy[foundItemIndex].guests = guests.slice(0, newGuests);
      }
      setParticipants(participantsCopy);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(
        getParticipantErrorMessage(e, 'EventsFormPage.addGuestError')
      );
    }
  };

  const markParticipantAttendance = async (participantId, attendedAmount) => {
    try {
      setLoading(true);
      const attended = attendedAmount > 0;
      await Api.events.markAttendance(attendedAmount, attended, participantId);
      toast.success(t('EventParticipantsPage.markedAttendance'));
      const participantsCopy = [...participants];
      const foundItemIndex = participantsCopy.findIndex(
        (item) => item.id === participantId
      );
      participantsCopy[foundItemIndex].status = attended
        ? 'ATTENDED'
        : 'ACCEPTED';
      participantsCopy[foundItemIndex].attendedAmount = attendedAmount;
      setParticipants(participantsCopy);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(getParticipantErrorMessage(e));
    }
  };
  const markMultipleAttendance = async (attendantIds) => {
    try {
      setLoading(true);
      await Promise.all(
        attendantIds.map((id) => Api.events.markAttendance(true, id))
      );
      toast.success(p('markedAttendance'));
      const participantsCopy = participants.map((item) => ({
        ...item,
        status: attendantIds.includes(item.id) ? 'ATTENDED' : item.status,
      }));
      setParticipants(participantsCopy);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e || t('EventParticipantsPage.eventNotFinished'));
    }
  };

  const saveAnswers = async (payload) => {
    setLoading(true);
    await Api.questions.saveAnswers(event.id, payload);
    await getPaticipants();
    setLoading(false);
  };

  return (
    <Container>
      <AlertModal
        visible={showPermissionModal}
        modalType={AlertModalType.ERROR}
        handleModal={() => setShowPermissionModal(false)}
        message={errorMessage}
      />
      <Loading loading={loading || loadingMount} />
      {event?.seriesId && <OccurrenceBanner event={event} />}
      <Row>
        <Col>
          <Card>
            <CardBody style={{ padding: 0 }}>
              <NavTab
                tabs={[
                  t('shared.event'),
                  {
                    tab: (
                      <>
                        {t('shared.communication')}
                        {numberOfPendingMessagesPerEvent > 0 && (
                          <Badge style={{ marginLeft: 5 }} color="primary">
                            {numberOfPendingMessagesPerEvent}
                          </Badge>
                        )}
                      </>
                    ),
                    disabled: !eventID,
                  },
                  { tab: t('shared.participants'), disabled: !eventID },
                  { tab: t('question.questions'), disabled: !eventID },
                  { tab: t('EventsPage.waitlist'), disabled: !eventID },
                  {
                    tab: t('comments.comments'),
                    disabled: !eventID || !commentsAccess?.visible,
                  },
                ]}
                activeTab={tab}
                setTab={setTab}
                size="sm"
                style={{ marginBottom: 0 }}
              />
              <TabContent activeTab={tab}>
                <TabPane style={{ padding: 10 }} tabId="1">
                  <EventsForm
                    eventForm={eventForm}
                    event={event}
                    commentsAccess={commentsAccess}
                    onSubmit={onSubmit}
                    eventCategories={eventCategories}
                    eventLevels={eventLevels}
                    eventGroups={eventGroups}
                    eventTiers={eventTiers}
                    setSelectedCountry={setSelectedCountry}
                    numberOfPendingMessagesPerEvent={
                      numberOfPendingMessagesPerEvent
                    }
                  />
                </TabPane>
                <TabPane style={{ padding: 10 }} tabId="2">
                  <EventsContactForm
                    onSubmit={sendMessage}
                    checkCount={checkCount}
                    eventID={eventID}
                    scheduleMessageID={scheduleMessageID}
                    setScheduleMessageID={setScheduleMessageID}
                    setScheduleMessagesForEvent={setScheduleMessagesForEvent}
                    scheduleMessagesForEvent={scheduleMessagesForEvent}
                    getScheduleMessagesByServiceId={
                      getScheduleMessagesByServiceId
                    }
                    totalAmountOfScheduledMessages={
                      totalAmountOfScheduledMessages
                    }
                    clearContactPage={clearContactPage}
                    setClearContactPage={setClearContactPage}
                    messageStatus={messageStatus}
                    setMessageStatus={setMessageStatus}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    setLoading={setLoading}
                    event={event}
                  />
                </TabPane>
                <TabPane style={{ padding: 10 }} tabId="3">
                  <EventsPariticipantForm
                    eventID={eventID}
                    event={event}
                    companies={companies}
                    setCompanies={setCompanies}
                    handleInviteParticipant={handleInviteParticipant}
                    inviteModelVisible={inviteModelVisible}
                    setInviteModelVisible={setInviteModelVisible}
                    changeGuestAmount={changeGuestAmount}
                    participants={participants}
                    setParticipants={setParticipants}
                    deletedParticipants={deletedParticipants}
                    setDeletedParticipants={setDeletedParticipants}
                    setLoading={setLoading}
                    getPaticipants={getPaticipants}
                    currentParticipant={currentParticipant}
                    setCurrentParticipant={setCurrentParticipant}
                    markParticipantAttendance={markParticipantAttendance}
                    markMultipleAttendance={markMultipleAttendance}
                    exportParticpants={exportParticpants}
                    exportParticpantsXLSX={exportParticpantsXLSX}
                    questionsNanswers={questionsNanswers}
                    questions={questions}
                    userAnswers={userAnswers}
                    saveAnswers={saveAnswers}
                    maxGuestAmount={maxGuestAmount}
                    setMaxGuestAmount={setMaxGuestAmount}
                  />
                </TabPane>

                <TabPane style={{ padding: 10 }} tabId="4">
                  <EventState>
                    <QuestionCard eventId={eventID} />
                  </EventState>
                </TabPane>
                <LazyTabPane activeTab={tab} tabId="5">
                  <WaitlistPage eventId={eventID} />
                </LazyTabPane>
                <LazyTabPane activeTab={tab} tabId="6">
                  <CommentsListPage entityId={eventID} />
                </LazyTabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default withRouter(
  connect((state) => ({
    eventForm: getFormValues('events_form')(state),
  }))(EventsFormPage)
);
